import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { productService, communityService, notificationService, activityService } from '../services/api';
import { useCategories } from '../hooks/useCategories';
import { getImageUrl } from '../utils/imageUtils';
import { useAuth } from '../hooks/useAuth';
import { useWishlist } from '../hooks/useWishlist';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ProductCard from '../components/ProductCard';
import { ACTIVE_THEME, getThemeVars } from '../themes/dashboardThemes';
import DashboardSidebar from '../components/DashboardSidebar';
// import { TOTAL, generateStickers } from '../album/albumData'; // Album 2026 card (hidden)

const COMMUNITY_ICONS = ['school', 'palette', 'account_balance', 'groups', 'park', 'work'];

function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [searchParams, setSearchParams] = useSearchParams();
  const [searchInput, setSearchInput] = useState(() => searchParams.get('q') || '');
  const [appliedQuery, setAppliedQuery] = useState(() => searchParams.get('q') || '');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedCommunities, setSelectedCommunities] = useState([]);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [communityOpen, setCommunityOpen] = useState(false);
  const [barVariant, setBarVariant] = useState(0);

  const categoryRef = useRef(null);
  const communityRef = useRef(null);
  const [selectedSort, setSelectedSort] = useState('RECENT');
  const [sortOpen, setSortOpen] = useState(false);
  const sortRef = useRef(null);

  const VARIANT_NAMES = ['Glass Bold + Subtle Glass Cards', 'Glass Bold Shadow', 'Glass Elevated', 'Mercado Libre', 'Glass Bold + Glass Cards'];

  const cardVariant = barVariant === 0 ? 'glass-subtle' : barVariant === 3 ? 'mercadolibre' : barVariant === 4 ? 'glass' : 'default';
  const shellBgOverride = barVariant === 3 ? '#f1f1f1' : null;

  const SORT_OPTIONS = useMemo(() => [
    { value: 'RECENT',      label: t('Más nuevos') },
    { value: 'PRICE_ASC',   label: t('Precio: menor a mayor') },
    { value: 'PRICE_DESC',  label: t('Precio: mayor a menor') },
    { value: 'MOST_VIEWED', label: t('Más vistos') },
  ], [t]);

  const theme = getThemeVars(ACTIVE_THEME);

  const parseActivityTimestamp = (timestamp) => {
    if (!timestamp) return null;
    // Backend serializes LocalDateTime as array [year, month, day, hour, min, sec]
    if (Array.isArray(timestamp)) {
      const [y, mo, d, h = 0, m = 0, s = 0] = timestamp;
      return new Date(y, mo - 1, d, h, m, s);
    }
    return new Date(timestamp);
  };

  const formatActivityTime = (timestamp) => {
    const date = parseActivityTimestamp(timestamp);
    if (!date || isNaN(date.getTime())) return '';
    const diff = Date.now() - date.getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return t('Recién');
    if (mins === 1) return t('Hace 1 minuto');
    if (mins < 60) return t('Hace {{count}} minutos', { count: mins });
    const hrs = Math.floor(mins / 60);
    if (hrs === 1) return t('Hace 1 hora');
    if (hrs < 24) return t('Hace {{count}} horas', { count: hrs });
    const days = Math.floor(hrs / 24);
    if (days === 1) return t('Ayer');
    return t('Hace {{count}} días', { count: days });
  };

  // ── Data queries (fetch once, filter client-side) ───────────────────────
  const { data: communities = [], isLoading: communitiesLoading } = useQuery({
    queryKey: ['userCommunities'],
    queryFn: async () => { const r = await communityService.getUserCommunities(); return r.data; },
  });


  // Auto-select community from URL ?community=name
  useEffect(() => {
    const communityName = searchParams.get('community');
    if (communityName && communities.length > 0) {
      const match = communities.find((c) => c.name === communityName);
      if (match) setSelectedCommunities([match]);
      searchParams.delete('community');
      setSearchParams(searchParams, { replace: true });
    }
  }, [communities, searchParams]);

  // Sync applied query + input when URL ?q= param changes
  useEffect(() => {
    const q = searchParams.get('q') || '';
    setAppliedQuery(q);
    setSearchInput(q);
  }, [searchParams.get('q')]);

  const { categories } = useCategories();

  const communityIdsFilter = selectedCommunities.map((c) => c.id);

  const {
    data: productPages,
    isLoading: productsLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['productCards', { q: appliedQuery || undefined, categoryId: selectedCategory?.id, communityIds: communityIdsFilter.length > 0 ? communityIdsFilter : undefined, sortBy: selectedSort }],
    queryFn: async ({ pageParam }) => {
      const r = await productService.getProductCards({
        pageSize: 16,
        cursor: pageParam,
        q: appliedQuery || undefined,
        categoryId: selectedCategory?.id,
        communityIds: communityIdsFilter.length > 0 ? communityIdsFilter : undefined,
        sortBy: selectedSort,
      });
      return r.data;
    },
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => lastPage?.hasMore ? lastPage.nextCursor : undefined,
  });

  const products = useMemo(
    () => productPages?.pages?.flatMap((p) => Array.isArray(p) ? p : (p.data ?? p.items ?? [])) ?? [],
    [productPages],
  );

  // Infinite scroll sentinel
  const sentinelRef = useRef(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) fetchNextPage(); },
      { rootMargin: '400px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const { isWishlisted, toggleWishlist } = useWishlist();

  const { data: notifications = [] } = useQuery({
    queryKey: ['notifications'],
    queryFn: async () => {
      const r = await notificationService.getNotifications();
      const data = r.data;
      if (data?.content) return data.content.slice(0, 5);
      if (Array.isArray(data)) return data.slice(0, 5);
      return [];
    },
    enabled: !!user,
  });

  const { data: recentActivity = [] } = useQuery({
    queryKey: ['recentActivity'],
    queryFn: async () => {
      const r = await activityService.getRecentActivity(5);
      return (r.data || []).slice(0, 5);
    },
    enabled: !!user,
  });

  // ── Close dropdowns on click outside ────────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (categoryRef.current && !categoryRef.current.contains(e.target)) setCategoryOpen(false);
      if (communityRef.current && !communityRef.current.contains(e.target)) setCommunityOpen(false);
      if (sortRef.current && !sortRef.current.contains(e.target)) setSortOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    const q = searchInput.trim();
    setAppliedQuery(q);
    setSearchParams(prev => {
      const p = new URLSearchParams(prev);
      if (q) p.set('q', q); else p.delete('q');
      return p;
    });
  };

  const clearSearch = () => {
    setSearchInput('');
    setAppliedQuery('');
    setSearchParams(prev => { const p = new URLSearchParams(prev); p.delete('q'); return p; });
  };

  const toggleCommunityFilter = (community) => {
    setSelectedCommunities((prev) =>
      prev.some((c) => c.id === community.id)
        ? prev.filter((c) => c.id !== community.id)
        : [...prev, community]
    );
  };

  const hasActiveFilters = appliedQuery || selectedCategory || selectedCommunities.length > 0 || selectedSort !== 'RECENT';

  // ── Album progress (hidden until design review) ─────────────────────────
  // const albumProgress = useMemo(() => {
  //   const stickers = generateStickers(42);
  //   const owned = stickers.filter((s) => s.owned).length;
  //   const reps = stickers.filter((s) => s.reps >= 2).reduce((sum, s) => sum + s.reps, 0);
  //   return { owned, total: TOTAL, reps, pct: Math.round((owned / TOTAL) * 100) };
  // }, []);

  // ── Shared dropdown renderers (popups are identical across variants) ──
  const categoryDropdown = categoryOpen && (
    <div className="absolute top-full left-0 mt-2 w-[280px] bg-surface-container rounded-xl shadow-xl border border-outline-variant/20 p-1.5 z-50 max-h-[420px] overflow-y-auto">
      <button onClick={() => { setSelectedCategory(null); setCategoryOpen(false); }} className={`w-full text-left px-4 py-2.5 text-[13px] rounded-xl transition-colors text-on-surface ${!selectedCategory ? 'bg-primary-container/30 font-bold' : 'font-medium hover:bg-surface-container-high'}`}>{t('Todas las categorías')}</button>
      <div className="mx-3 my-1.5 border-t border-outline-variant/15" />
      {categories.map((cat) => (
        <button key={cat.id} onClick={() => { setSelectedCategory(cat); setCategoryOpen(false); }} className={`w-full text-left px-4 py-2.5 text-[13px] rounded-xl transition-colors text-on-surface ${selectedCategory?.id === cat.id ? 'bg-primary-container/30 font-bold' : 'font-medium hover:bg-surface-container-high'}`}>{cat.name}</button>
      ))}
    </div>
  );

  const communityDropdown = communityOpen && (
    <div className="absolute top-full right-0 mt-2 w-[300px] bg-surface-container rounded-xl shadow-xl border border-outline-variant/20 p-1.5 z-50 max-h-[420px] overflow-y-auto">
      {communities.length === 0 ? (
        <p className="px-4 py-4 text-xs text-on-surface-variant">{t('No perteneces a ninguna comunidad')}</p>
      ) : (
        <>
          {selectedCommunities.length > 0 && (
            <button onClick={() => setSelectedCommunities([])} className="w-full flex items-center gap-2.5 px-4 py-2.5 rounded-xl text-left text-xs text-primary font-semibold hover:bg-surface-container-high transition-colors mb-0.5">
              <span className="material-symbols-outlined text-[16px]">restart_alt</span>{t('Todas las comunidades')}
            </button>
          )}
          <div className="mx-3 my-1 border-t border-outline-variant/15" />
          {communities.map((c) => {
            const isSel = selectedCommunities.some(sc => sc.id === c.id);
            return (
              <button key={c.id} onClick={() => toggleCommunityFilter(c)} className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-xl transition-colors ${isSel ? 'bg-primary-container/30' : 'hover:bg-surface-container-high'}`}>
                <div className={`w-[18px] h-[18px] rounded flex items-center justify-center shrink-0 transition-colors ${isSel ? 'bg-primary' : 'border-2 border-outline-variant'}`}>
                  {isSel && <span className="material-symbols-outlined text-[13px] text-on-primary">check</span>}
                </div>
                <span className={`text-[13px] text-on-surface truncate ${isSel ? 'font-bold' : 'font-medium'}`}>{c.name}</span>
              </button>
            );
          })}
        </>
      )}
    </div>
  );

  const sortDropdown = sortOpen && (
    <div className="absolute top-full right-0 mt-2 w-[220px] bg-surface-container rounded-xl shadow-xl border border-outline-variant/20 p-1.5 z-50">
      {SORT_OPTIONS.map(opt => (
        <button key={opt.value} onClick={() => { setSelectedSort(opt.value); setSortOpen(false); }} className={`w-full text-left px-4 py-2.5 text-[13px] rounded-xl transition-colors text-on-surface ${selectedSort === opt.value ? 'bg-primary-container/30 font-bold' : 'font-medium hover:bg-surface-container-high'}`}>{opt.label}</button>
      ))}
    </div>
  );

  const openCat = () => { setCategoryOpen(o => !o); setCommunityOpen(false); setSortOpen(false); };
  const openComm = () => { setCommunityOpen(o => !o); setCategoryOpen(false); setSortOpen(false); };
  const openSort = () => { setSortOpen(o => !o); setCategoryOpen(false); setCommunityOpen(false); };
  const clearAll = () => { clearSearch(); setSelectedCategory(null); setSelectedCommunities([]); setSelectedSort('RECENT'); };

  const commLabel = selectedCommunities.length > 0
    ? selectedCommunities.length === 1 ? selectedCommunities[0].name : `${selectedCommunities.length} ${t('comunidades')}`
    : t('Comunidades');
  const sortLabel = SORT_OPTIONS.find(o => o.value === selectedSort)?.label ?? t('Ordenar');

  // ── Pill renderers (shared across single-row variants) ──
  const renderPills = (pB, pA, pI) => (
    <div className="hidden sm:flex items-center gap-2 px-2">
      <div className="relative" ref={categoryRef}>
        <div onClick={openCat} className={`${pB} ${selectedCategory ? pA : pI}`}>
          <span className="material-symbols-outlined text-sm">category</span>
          {selectedCategory ? selectedCategory.name : t('Categoría')}
          <span className="material-symbols-outlined text-[14px]">expand_more</span>
        </div>
        {categoryDropdown}
      </div>
      <div className="relative" ref={communityRef}>
        <div onClick={openComm} className={`${pB} ${selectedCommunities.length > 0 ? pA : pI}`}>
          <span className="material-symbols-outlined text-sm">group</span>
          {commLabel}
          <span className="material-symbols-outlined text-[14px]">expand_more</span>
        </div>
        {communityDropdown}
      </div>
      <div className="relative" ref={sortRef}>
        <div onClick={openSort} className={`${pB} ${selectedSort !== 'RECENT' ? pA : pI}`}>
          <span className="material-symbols-outlined text-sm">swap_vert</span>
          {sortLabel}
          <span className="material-symbols-outlined text-[14px]">expand_more</span>
        </div>
        {sortDropdown}
      </div>
      {hasActiveFilters && (
        <div onClick={clearAll} className={`${pB} text-error hover:bg-error/10`}>
          <span className="material-symbols-outlined text-sm">restart_alt</span>
        </div>
      )}
    </div>
  );

  const searchHandler = (e) => { if (e.key === 'Enter') handleSearchSubmit(e); };
  const pB0 = 'flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-full cursor-pointer transition-colors whitespace-nowrap';

  // ── Search bar renderer ──
  const renderSearchBar = () => {
    // --- Variant 2: Glass Elevated ---
    if (barVariant === 2) {
      return (
        <div className="px-4 tablet-sm:px-10 py-6 sticky top-0 bg-background backdrop-blur-2xl z-40 shrink-0">
          <div className="flex items-center gap-4 bg-white/60 backdrop-blur-lg rounded-2xl p-3 shadow-2xl shadow-primary/10 ring-1 ring-white/40 transition-all duration-300 focus-within:ring-2 focus-within:ring-primary/40 focus-within:shadow-primary/20">
            <div className="flex flex-1 items-center px-4 gap-3 border-r border-white/20">
              <span className="material-symbols-outlined text-on-surface-variant/70 text-[20px]">search</span>
              <input className="bg-transparent border-none focus:ring-0 focus:outline-none w-full text-sm font-medium placeholder:text-on-surface-variant/50 text-on-surface" placeholder={t('Buscar productos...')} type="text" value={searchInput} onChange={(e) => setSearchInput(e.target.value)} onKeyDown={searchHandler} />
              {appliedQuery && <button onClick={clearSearch} className="shrink-0 hover:opacity-60"><span className="material-symbols-outlined text-[16px] text-on-surface-variant/60">close</span></button>}
            </div>
            {renderPills(pB0,
              'bg-primary/20 backdrop-blur-md text-primary border border-primary/40',
              'bg-white/40 backdrop-blur-md text-on-surface-variant border border-white/30 hover:bg-white/50'
            )}
          </div>
        </div>
      );
    }

    // --- Variant 3: Mercado Libre style ---
    if (barVariant === 3) {
      return (
        <div className="px-4 tablet-sm:px-10 py-6 sticky top-0 z-40 shrink-0" style={{ backgroundColor: '#f1f1f1' }}>
          <div className="flex items-center gap-4 bg-white rounded-md p-3 shadow-[0_1px_2px_rgba(0,0,0,0.10)] border border-black/[0.04] transition-all duration-200 focus-within:shadow-[0_2px_8px_rgba(0,0,0,0.14)]">
            <div className="flex flex-1 items-center px-4 gap-3 border-r border-black/[0.06]">
              <span className="material-symbols-outlined text-on-surface-variant/70 text-[20px]">search</span>
              <input className="bg-transparent border-none focus:ring-0 focus:outline-none w-full text-sm font-medium placeholder:text-on-surface-variant/50 text-on-surface" placeholder={t('Buscar productos...')} type="text" value={searchInput} onChange={(e) => setSearchInput(e.target.value)} onKeyDown={searchHandler} />
              {appliedQuery && <button onClick={clearSearch} className="shrink-0 hover:opacity-60"><span className="material-symbols-outlined text-[16px] text-on-surface-variant/60">close</span></button>}
            </div>
            {renderPills(pB0,
              'bg-primary/15 text-primary border border-primary/40',
              'bg-black/[0.03] text-on-surface-variant border border-black/[0.06] hover:bg-black/[0.06]'
            )}
          </div>
        </div>
      );
    }

    // --- Variant 0, 1 & 4: Glass Bold Shadow (0 = subtle glass cards, 1 = default cards, 4 = glass cards) ---
    return (
      <div className="px-4 tablet-sm:px-10 py-6 sticky top-0 bg-background backdrop-blur-2xl z-40 shrink-0">
        <div className="flex items-center gap-4 bg-white/80 backdrop-blur-lg rounded-2xl p-3 shadow-[0_8px_32px_rgba(0,0,0,0.12),0_2px_8px_rgba(0,0,0,0.08)] ring-1 ring-black/[0.04] border border-black/[0.06] transition-all duration-300 focus-within:shadow-[0_8px_32px_rgba(37,99,235,0.18),0_2px_8px_rgba(0,0,0,0.10)] focus-within:ring-2 focus-within:ring-primary/40">
          <div className="flex flex-1 items-center px-4 gap-3 border-r border-black/[0.06]">
            <span className="material-symbols-outlined text-on-surface-variant/70 text-[20px]">search</span>
            <input className="bg-transparent border-none focus:ring-0 focus:outline-none w-full text-sm font-medium placeholder:text-on-surface-variant/50 text-on-surface" placeholder={t('Buscar productos...')} type="text" value={searchInput} onChange={(e) => setSearchInput(e.target.value)} onKeyDown={searchHandler} />
            {appliedQuery && <button onClick={clearSearch} className="shrink-0 hover:opacity-60"><span className="material-symbols-outlined text-[16px] text-on-surface-variant/60">close</span></button>}
          </div>
          {renderPills(pB0,
            'bg-primary/20 backdrop-blur-md text-primary border border-primary/40',
            'bg-white/50 backdrop-blur-md text-on-surface-variant border border-black/[0.06] hover:bg-white/70'
          )}
        </div>
      </div>
    );
  };

  return (
    <div
      className="dashboard-new dashboard-shell font-body selection:bg-primary-container selection:text-on-primary-container flex h-[100dvh] overflow-hidden"
      style={{ ...theme, backgroundColor: shellBgOverride || 'var(--chrome-bg)', color: 'var(--chrome-text)' }}
    >
      <DashboardSidebar activePath="/" />

      <div className="flex-1 flex flex-row min-w-0 overflow-hidden">
        {/* ── Product column: search bar + grid ── */}
        <div className="flex-1 flex flex-col min-w-0 overflow-hidden">

          {renderSearchBar()}

          {/* ── Product Grid ── */}
          <div className="flex-1 overflow-y-auto hide-scrollbar cm-pb-mobile-nav">

            {/* Banner: usuario sin comunidades. Lo invitamos a /communities para
                que sume su mail institucional o cree una propia. Aparece sólo
                cuando ya cargaron las comunidades y no tiene ninguna. */}
            {!communitiesLoading && communities.length === 0 && (
              <div className="px-4 tablet-md:px-8 pt-5">
                <div className="bg-primary-container/40 border border-primary/30 rounded-2xl px-4 py-3 flex flex-col mobile-sm:flex-row items-start mobile-sm:items-center gap-3">
                  <span className="material-symbols-outlined text-primary text-2xl shrink-0">groups</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-on-surface">{t('Aún no perteneces a ninguna comunidad')}</p>
                    <p className="text-xs text-on-surface-variant">{t('Sumate a una para ver productos de tu organización o crear la tuya.')}</p>
                  </div>
                  <button
                    onClick={() => navigate('/communities')}
                    className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary text-on-primary text-xs font-bold rounded-xl hover:opacity-90 transition-opacity"
                  >
                    {t('Ver Comunidades')}
                    <span className="material-symbols-outlined text-sm">arrow_forward</span>
                  </button>
                </div>
              </div>
            )}

            {productsLoading ? (
              <div className="grid grid-cols-2 tablet-sm:grid-cols-3 tablet-md:grid-cols-4 min-[1700px]:grid-cols-5 gap-4 px-4 tablet-md:px-8 py-5">
                {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                  <div key={i} className="bg-surface-container rounded-xl overflow-hidden shadow-sm">
                    <div className="aspect-square bg-surface-container-high animate-pulse" style={{ aspectRatio: '1 / 1' }} />
                    <div className="p-3 space-y-2">
                      <div className="h-4 bg-surface-container-high rounded animate-pulse w-3/4" />
                      <div className="h-3 bg-surface-container-high rounded animate-pulse w-1/2" />
                    </div>
                  </div>
                ))}
              </div>

            ) : products.length > 0 ? (
              <>
                <div className="grid grid-cols-2 tablet-sm:grid-cols-3 tablet-md:grid-cols-4 min-[1700px]:grid-cols-5 gap-4 px-4 tablet-md:px-8 py-5">
                  {products.map((product) => (
                    <ProductCard
                      key={product.id}
                      product={product}
                      onClick={() => navigate(`/products/${product.id}`)}
                      wishlisted={isWishlisted(product.id)}
                      onWishlistToggle={(p) => toggleWishlist(p.id, p)}
                      variant={cardVariant}
                    />
                  ))}
                </div>

                {/* Infinite scroll sentinel */}
                <div ref={sentinelRef} className="flex justify-center py-8">
                  {isFetchingNextPage && (
                    <div className="flex items-center gap-2 text-on-surface-variant text-sm">
                      <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                    </div>
                  )}
                </div>
              </>

            ) : (
              <div className="flex flex-col items-center justify-center py-20 text-center">
                <span className="material-symbols-outlined text-6xl text-outline-variant mb-4">
                  {hasActiveFilters ? 'search_off' : 'storefront'}
                </span>
                <h3 className="text-lg font-bold text-on-surface mb-2">
                  {hasActiveFilters ? t('No se encontraron productos') : t('No hay productos disponibles')}
                </h3>
                <p className="text-sm text-on-surface-variant mb-6">
                  {hasActiveFilters
                    ? t('Probá ajustando los filtros o la búsqueda')
                    : t('Únete a comunidades para ver productos o comienza a vender')
                  }
                </p>
                {hasActiveFilters ? (
                  <button
                    onClick={() => { setSelectedCategory(null); setSelectedCommunities([]); setSelectedSort('RECENT'); clearSearch(); }}
                    className="px-6 py-2.5 rounded-xl bg-primary text-on-primary font-medium hover:opacity-90 transition-opacity"
                  >
                    {t('Limpiar filtros')}
                  </button>
                ) : (
                  <div className="flex gap-3">
                    <button onClick={() => navigate('/communities')} className="px-4 py-2 rounded-xl border border-outline text-on-surface font-medium hover:bg-surface-container transition-colors">
                      {t('Explorar Comunidades')}
                    </button>
                    <button onClick={() => navigate('/create-product')} className="px-4 py-2 rounded-xl bg-primary text-on-primary font-medium hover:opacity-90 transition-opacity">
                      {t('Vender Producto')}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* (FAB de "+" eliminado: el bottom tab bar mobile ya tiene un CTA
               "+" central que cumple la misma función. Mantener ambos creaba
               un cuarto de círculo cortado pegado al borde derecho.) */}

        </div>

        {/* ═══ RIGHT SIDEBAR ═══ */}
        {/* Aparece a partir de 1400px de viewport. La 5ª col del grid entra
            recién a 1700px — entre 1400 y 1700 hay sidebar + 4 cols cómodas;
            debajo de 1400 (zoom alto o pantallas con DPI scaling fuerte) el
            sidebar se oculta y el feed absorbe los 288px para mantener 4
            cards bien grandes. */}
        <aside
          className="hidden min-[1400px]:flex h-full w-72 flex-col p-6 z-40 overflow-y-auto shrink-0"
          style={{ backgroundColor: 'var(--chrome-right-sidebar)', borderLeft: '1px solid var(--chrome-border-subtle)' }}
        >
          {/* Communities */}
          <section className="mb-10">
            <h4 className="text-xs font-bold uppercase tracking-widest mb-5 flex items-center justify-between" style={{ color: 'var(--chrome-label)' }}>
              {t('Comunidades')}
              <span onClick={() => navigate('/communities')} className="material-symbols-outlined text-sm cursor-pointer" style={{ color: 'var(--chrome-label)' }}>more_horiz</span>
            </h4>
            <div className="space-y-4">
              {communities.length > 0 ? communities.slice(0, 3).map((c, i) => {
                const bgColors = ['bg-primary-container', 'bg-secondary-container', 'bg-primary-container/40'];
                const textColors = ['text-on-primary-container', 'text-on-secondary-container', 'text-on-primary-container'];
                const subtitleColors = ['text-primary', 'text-secondary', 'text-primary'];
                return (
                  <div
                    key={c.id}
                    onClick={() => navigate(`/communities/${c.id}`)}
                    className={`flex items-center gap-3 group cursor-pointer ${i > 0 ? 'opacity-70 hover:opacity-100 transition-opacity' : ''}`}
                  >
                    <div className={`w-10 h-10 rounded-xl ${bgColors[i % 3]} flex items-center justify-center ${textColors[i % 3]} shrink-0`}>
                      {c.imageId
                        ? <img alt={c.name} className="w-10 h-10 rounded-xl object-cover" src={getImageUrl(c.imageId)} />
                        : <span className="material-symbols-outlined">{COMMUNITY_ICONS[i % COMMUNITY_ICONS.length]}</span>
                      }
                    </div>
                    <div className="flex-1 overflow-hidden">
                      <p className="text-sm font-bold text-on-surface truncate">{c.name}</p>
                      <p className={`text-[10px] ${subtitleColors[i % 3]} font-medium`}>{t('{{count}} miembros', { count: c.memberCount })}</p>
                    </div>
                  </div>
                );
              }) : (
                <p className="text-xs text-on-surface-variant">{t('Join a community to see it here')}</p>
              )}
            </div>
          </section>

          {/* Recent Activity */}
          {recentActivity.length > 0 && (
            <section className="flex-1 overflow-y-auto hide-scrollbar">
              <h4 className="text-lg font-bold tracking-tight mb-6" style={{ color: 'var(--chrome-strong)' }}>{t('Actividad reciente')}</h4>
              <div className="space-y-6">
                {recentActivity.map((event, i) => {
                  const isLast = i === recentActivity.length - 1;
                  const isMe = user && event.actorId === user.id;
                  const displayName = isMe ? t('Vos') : (event.actorName || '?');
                  const initial = (event.actorName || '?').charAt(0).toUpperCase();
                  const timeLabel = formatActivityTime(event.timestamp);

                  let actionText;
                  if (event.type === 'NEW_PRODUCT') {
                    actionText = (
                      <> {t('publicó')} <span className="font-bold" style={{ color: 'var(--chrome-strong)' }}>{event.productName}</span>
                      {event.communityName && <> {t('en')} <span className="font-bold" style={{ color: 'var(--chrome-strong)' }}>{event.communityName}</span></>}
                      </>
                    );
                  } else if (event.type === 'MEMBER_JOINED') {
                    actionText = (
                      <> {t('se unió a')} <span className="font-bold" style={{ color: 'var(--chrome-strong)' }}>{event.communityName}</span></>
                    );
                  } else if (event.type === 'RATING_RECEIVED') {
                    actionText = (
                      <> {t('te calificó')} <span className="font-bold" style={{ color: 'var(--chrome-strong)' }}>{event.rating}★</span>
                      {event.productName && <> {t('por')} <span className="font-bold" style={{ color: 'var(--chrome-strong)' }}>{event.productName}</span></>}
                      </>
                    );
                  }

                  return (
                    <div key={`${event.actorId}-${event.timestamp}`} className="relative flex gap-4">
                      {!isLast && <div className="absolute left-[15px] top-8 -bottom-6 w-0.5" style={{ backgroundColor: 'var(--chrome-border-divider)' }} />}
                      <div className="relative shrink-0">
                        <div className="w-8 h-8 rounded-full border border-white/50 flex items-center justify-center text-white text-xs font-bold" style={{ backgroundColor: 'var(--chrome-avatar-accent)' }}>{initial}</div>
                      </div>
                      <div className={`flex-1 ${!isLast ? 'pb-0' : ''}`}>
                        <p className="text-xs leading-snug" style={{ color: 'var(--chrome-body)' }}>
                          <span className="font-bold" style={{ color: 'var(--chrome-strong)' }}>{displayName}</span>{actionText}
                        </p>
                        <span className="text-[9px] font-bold tracking-wider mt-1 block uppercase" style={{ color: 'var(--chrome-label)' }}>{timeLabel}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          )}
        </aside>
      </div>
    </div>
  );
}

export default Dashboard;
